import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import MobileAnimatedModal from '@/features/shared/components/MobileAnimatedModal';
import { Music, Plus, Edit, Trash2, GripVertical, CheckCircle, X, ArrowLeft, Archive, RotateCcw, ListMusic, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useNavigate, useSearchParams } from 'react-router-dom';

// ✅ IMPORT THE NEW FIREBASE AUTH CONTEXT AND SERVICES
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { getMinimumPrice, formatMinimumPrice, getTestSongMinimumPrice, formatTestSongMinimumPrice } from '@/lib/currencyConfig';
import { formatMoney } from '@/lib/currency';
import { db, functions } from '@/lib/firebase';
import { updateReferralSignupStage } from '@/lib/referralSystem';
import { 
    collection, query, where, getDocs, 
    addDoc, updateDoc, deleteDoc, doc, writeBatch, serverTimestamp 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import MobileCatalogue from '@/features/shared/components/MobileCatalogue';
import AddSongWizard from '@/features/shared/components/AddSongWizard';
import SetlistWizard from '@/features/shared/components/SetlistWizard';
import { capitalizeWords } from '@/lib/utils';
import {
  isAddSongWizardCompleted,
  ensureWizardDoneIfHasSongs,
  ADD_SONG_WIZARD_DISMISSED_KEY,
} from '@/lib/addSongWizardState';
import PopularSongsCatalogueSettings from '@/features/shared/components/PopularSongsCatalogueSettings';
import BatchSongPricesButton from '@/features/shared/components/BatchSongPricesButton';

const SetlistPage = () => {
    console.log('🔍 SetlistPage: Component rendered');
    
    // ✅ USEAUTH NOW PULLS FROM FIREBASE
    const { user, profile, refreshProfile } = useAuth();
    const mobile = useMobile();
    const { toast } = useToast();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Safely get currency context with fallback
    let currencyContext = null;
    try {
        currencyContext = useCurrency();
    } catch (error) {
        // Context not available, use fallback
        console.warn('CurrencyContext not available, using fallback format:', error);
    }
    const format = currencyContext?.format || ((amount) => formatMoney(amount ?? 0, currency));
    const currency = currencyContext?.currency || 'GBP';
    const minimumPrice = getMinimumPrice(currency);
    const formattedMinimum = formatMinimumPrice(currency);
    const testSongMinimum = getTestSongMinimumPrice(currency);
    
    console.log('🔍 SetlistPage: State initialized', { 
        user: !!user, 
        mobile: mobile.isMobile,
        userId: user?.uid 
    });
    const [songs, setSongs] = useState([]);
    const [archivedSongs, setArchivedSongs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingSong, setEditingSong] = useState(null);
    const [formData, setFormData] = useState({ title: '', original_artist: '', price: '' });
    const [multiSongMode, setMultiSongMode] = useState(false);
    const [addedSongs, setAddedSongs] = useState([]);
    const [showArchived, setShowArchived] = useState(false);
    const [showAddOptions, setShowAddOptions] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [showSongWizard, setShowSongWizard] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [showRapidUploader, setShowRapidUploader] = useState(false);
    const [isUploadingRapid, setIsUploadingRapid] = useState(false);
    const [activeTab, setActiveTab] = useState('songs'); // 'songs' or 'setlists'
    const [setlists, setSetlists] = useState([]);
    const [showCreateSetlist, setShowCreateSetlist] = useState(false);
    const [editingSetlist, setEditingSetlist] = useState(null);
    const [setlistFormData, setSetlistFormData] = useState({ name: '', selectedSongs: [] });
    const [showSetlistWizard, setShowSetlistWizard] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const isTestSongForm =
        formData.title.trim().toLowerCase() === 'test' &&
        formData.original_artist.trim().toLowerCase() === 'test';
    const isFreeSongForm =
        formData.title.trim().toLowerCase() === 'free' &&
        formData.original_artist.trim().toLowerCase() === 'free';
    const effectivePriceMin = isFreeSongForm ? 0 : isTestSongForm ? testSongMinimum : minimumPrice;
    const effectiveMinDisplay = isFreeSongForm
        ? format(0)
        : isTestSongForm
            ? formatTestSongMinimumPrice(currency)
            : formattedMinimum;

    // ✅ REFACTORED: Load songs from Firestore
    const loadSongsAndTags = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            // Simplified query to avoid composite index requirement
            const songsQuery = query(
                collection(db, 'songs'),
                where('user_id', '==', user.uid)
                // Removed orderBy('order') to avoid composite index requirement
            );
            const querySnapshot = await getDocs(songsQuery);
            const songsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            // Sort in memory instead of requiring a composite index
            const sortedSongs = songsData.sort((a, b) => (a.order || 0) - (b.order || 0));
            
            const activeSongs = sortedSongs.filter(song => !song.archived);
            const archivedSongsData = sortedSongs.filter(song => song.archived);
            
            setSongs(activeSongs);
            setArchivedSongs(archivedSongsData);
            void ensureWizardDoneIfHasSongs(db, user.uid, activeSongs.length);
        } catch (error) {
            console.error("Error loading songs:", error);
            toast({ title: "Error", description: "Failed to load songs.", variant: "destructive" });
        } finally {
            setLoading(false);
        }
    }, [user, toast]);

    // ✅ Load setlists from Firestore
    const loadSetlists = useCallback(async () => {
        if (!user) return;
        console.log('🔍 loadSetlists called for user:', user.uid);
        try {
            const setlistsQuery = query(
                collection(db, 'playlists'),
                where('user_id', '==', user.uid)
            );
            const querySnapshot = await getDocs(setlistsQuery);
            const setlistsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const sortedSetlists = setlistsData.sort((a, b) => a.created_at?.toDate() - b.created_at?.toDate());
            console.log('🔍 Loaded setlists:', sortedSetlists);
            console.log('🔍 Setlists count:', sortedSetlists.length);
            setSetlists(sortedSetlists);
        } catch (error) {
            console.error("Error loading setlists:", error);
            toast({ title: "Error", description: "Failed to load setlists.", variant: "destructive" });
        }
    }, [user, toast]);

    useEffect(() => {
        loadSongsAndTags();
        loadSetlists();
    }, [loadSongsAndTags, loadSetlists]);

    // Debug setlists state changes
    useEffect(() => {
        console.log('🔍 setlists state changed:', setlists);
        console.log('🔍 setlists length:', setlists.length);
    }, [setlists]);

    // Debug mobile context
    useEffect(() => {
        console.log('🔍 Mobile context:', mobile);
        console.log('🔍 isMobile:', mobile.isMobile);
    }, [mobile]);

    // Handle URL parameter for tab
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam === 'setlists') {
            setActiveTab('setlists');
        }
    }, [searchParams]);

    // Catalogue: show add-song wizard for new artists (nav trigger, empty catalogue, or <5 after onboarding).
    useEffect(() => {
        const shouldShowWizard = localStorage.getItem('ysr_show_song_wizard') === 'true';
        const onboardingCompleted = localStorage.getItem('ysr_onboarding_completed') === 'true';
        const wizardDismissed = localStorage.getItem(ADD_SONG_WIZARD_DISMISSED_KEY) === 'true';
        const wizardDone = isAddSongWizardCompleted(profile, songs.length);

        if (wizardDone) {
            if (shouldShowWizard) localStorage.removeItem('ysr_show_song_wizard');
            setShowSongWizard(false);
            return;
        }

        if (shouldShowWizard && onboardingCompleted) {
            console.log('🎯 SetlistPage: Showing song wizard (catalogue / first-run trigger)');
            setShowSongWizard(true);
            localStorage.removeItem('ysr_show_song_wizard');
            return;
        }

        if (
            mobile.isMobile &&
            onboardingCompleted &&
            songs.length === 0 &&
            !wizardDismissed
        ) {
            setShowSongWizard(true);
        }
    }, [songs.length, profile, mobile.isMobile]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const saveAllSongs = async () => {
        if (!user) return;

        try {
            const batch = writeBatch(db);
            let order = songs.length + 1;
            let songsToSave = [...addedSongs];

            // If no songs in list but form has data, add the current form data
            if (songsToSave.length === 0 && formData.title.trim() && formData.original_artist.trim() && formData.price) {
                songsToSave.push({
                    title: formData.title.trim(),
                    original_artist: formData.original_artist.trim(),
                    price: parseFloat(formData.price)
                });
            }

            if (songsToSave.length === 0) return;

            // Validate all songs have valid prices before saving
            const invalidSongs = [];
            for (const song of songsToSave) {
                const price = parseFloat(song.price) || 0;
                const isTestSong = song.title.trim().toLowerCase() === 'test' && 
                                  song.original_artist.trim().toLowerCase() === 'test';
                // Workaround: Allow £0.00 if both title and artist are "free" (case-insensitive)
                const isFreeSong = song.title.trim().toLowerCase() === 'free' && 
                                  song.original_artist.trim().toLowerCase() === 'free';
                
                if (isNaN(price) || price < 0) {
                    invalidSongs.push({
                        title: song.title.trim(),
                        artist: song.original_artist.trim(),
                        price: price,
                        error: 'Price cannot be negative'
                    });
                } else if (isFreeSong && price >= 0) {
                    // Free song - allow £0.00, no validation needed
                    continue;
                } else if (!isTestSong && !isFreeSong && (isNaN(price) || price < minimumPrice)) {
                    invalidSongs.push({
                        title: song.title.trim(),
                        artist: song.original_artist.trim(),
                        price: price
                    });
                } else if (isTestSong && (isNaN(price) || price < testSongMinimum)) {
                    invalidSongs.push({
                        title: song.title.trim(),
                        artist: song.original_artist.trim(),
                        price: price,
                        isTest: true
                    });
                }
            }

            if (invalidSongs.length > 0) {
                const songDetails = invalidSongs.map(s => 
                    `"${s.title}" by ${s.artist} - Currently ${format(s.price)}, needs to be at least ${s.isTest ? format(testSongMinimum) : formattedMinimum}`
                ).join('\n');

                toast({
                    title: "⚠️ Price Too Low - Cannot Save",
                    description: `Some songs are priced below the minimum requirement of ${formattedMinimum} per song.\n\n${songDetails}\n\nPlease update all song prices to at least ${formattedMinimum} to continue. We recommend starting at ${format(minimumPrice)}-${format(minimumPrice + 1)} per request for better earnings.`,
                    variant: "destructive",
                    duration: 10000
                });
                return;
            }

            for (const song of songsToSave) {
                const songData = {
                    title: song.title,
                    original_artist: song.original_artist,
                    price: song.price,
                    user_id: user.uid,
                    created_at: serverTimestamp(),
                    order: order++,
                    archived: false
                };

                const songRef = doc(collection(db, 'songs'));
                batch.set(songRef, songData);
            }

            await batch.commit();
            
            // PWA install prompt removed - service workers disabled
            
            toast({ title: "Success!", description: `${songsToSave.length} song${songsToSave.length > 1 ? 's' : ''} added successfully!` });
            closeDialog();
            await loadSongsAndTags();
        } catch (error) {
            console.error("Error saving songs:", error);
            toast({ title: "Error", description: "Failed to save songs.", variant: "destructive" });
        }
    };

    const toggleSongExpansion = (songId) => {
        setAddedSongs(prev => prev.map(song => 
            song.id === songId ? { ...song, expanded: !song.expanded } : song
        ));
    };

    const removeAddedSong = (songId) => {
        setAddedSongs(prev => prev.filter(song => song.id !== songId));
    };



    const openDialog = (song = null) => {
        if (song) {
            setEditingSong(song);
            setFormData({
                title: song.title || '',
                original_artist: song.original_artist || '',
                price: song.price || ''
            });
        } else {
            setEditingSong(null);
            setFormData({ title: '', original_artist: '', price: '' });
        }
        setIsDialogOpen(true);
    };

    const openAddSongEntry = () => {
        const done = isAddSongWizardCompleted(profile, songs.length);
        if (!done && songs.length === 0) {
            setShowSongWizard(true);
            return;
        }
        openDialog();
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setEditingSong(null);
        setMultiSongMode(false);
        setAddedSongs([]);
        setFormData({ title: '', original_artist: '', price: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user) return;

        // Validate required fields
        if (!formData.title.trim() || !formData.original_artist.trim() || !formData.price) {
            toast({
                title: "Required Fields Missing",
                description: "Please fill in all required fields including price",
                variant: "destructive"
            });
            return;
        }

        const price = parseFloat(formData.price);
        
        // Test song: title+artist "test" — min from getTestSongMinimumPrice (USD $1, else e.g. £0.50)
        const isTestSong = formData.title.trim().toLowerCase() === 'test' && 
                          formData.original_artist.trim().toLowerCase() === 'test';
        
        if (!isTestSong && price < minimumPrice) {
            toast({
                title: "Minimum Price Required",
                description: `The minimum price for a song request is ${formattedMinimum}`,
                variant: "destructive"
            });
            return;
        }
        
        if (isTestSong && price < testSongMinimum) {
            toast({
                title: "Minimum Price Required",
                description: `The minimum price for a test song is ${format(testSongMinimum)}`,
                variant: "destructive"
            });
            return;
        }

        if (multiSongMode) {
            // Add song to the list
            const newSong = {
                id: Date.now().toString(),
                title: formData.title.trim(),
                original_artist: formData.original_artist.trim(),
                price: price,
                expanded: true
            };
            
            setAddedSongs(prev => [...prev, newSong]);
            setFormData({ title: '', original_artist: '', price: '' });
            return;
        }

        try {
        const songData = {
            user_id: user.uid,
                title: formData.title.trim(),
                original_artist: formData.original_artist.trim(),
                price: price,
                created_at: serverTimestamp()
        };

            if (editingSong) {
                await updateDoc(doc(db, 'songs', editingSong.id), songData);
                toast({ title: "Success!", description: "Song updated successfully." });
            } else {
                songData.order = songs.length;
                await addDoc(collection(db, 'songs'), songData);
                toast({ title: "Success!", description: "Song added successfully." });
            }

            closeDialog();
            await loadSongsAndTags();
        } catch (error) {
            console.error("Error saving song:", error);
            toast({ title: "Error", description: "Failed to save song.", variant: "destructive" });
        }
    };

    const deleteSong = async (songId) => {
        if (!user) return;

        try {
            await deleteDoc(doc(db, 'songs', songId));
            toast({ title: "Success!", description: "Song deleted successfully." });
            await loadSongsAndTags();
        } catch (error) {
            console.error("Error deleting song:", error);
            toast({ title: "Error", description: "Failed to delete song.", variant: "destructive" });
        }
    };

    const archiveSong = async (songId) => {
        if (!user) return;

        try {
            await updateDoc(doc(db, 'songs', songId), { archived: true });
            toast({ title: "Success!", description: "Song archived successfully." });
            await loadSongsAndTags();
        } catch (error) {
            console.error("Error archiving song:", error);
            toast({ title: "Error", description: "Failed to archive song.", variant: "destructive" });
        }
    };

    const restoreSong = async (songId) => {
        if (!user) return;

        try {
            await updateDoc(doc(db, 'songs', songId), { archived: false });
            toast({ title: "Success!", description: "Song restored successfully." });
            await loadSongsAndTags();
            } catch (error) {
            console.error("Error restoring song:", error);
            toast({ title: "Error", description: "Failed to restore song.", variant: "destructive" });
        }
    };

    const handleDragEnd = async (result) => {
        if (!result.destination || !user) return;
        
        // Don't do anything if the item was dropped in the same position
        if (result.destination.index === result.source.index) return;
        
        const items = Array.from(songs);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        
        // Update UI immediately for better user experience
        setSongs(items);

        try {
            // Update order in Firestore
            const batch = writeBatch(db);
            items.forEach((song, index) => {
                const songRef = doc(db, 'songs', song.id);
                batch.update(songRef, { order: index });
            });
            await batch.commit();

            await updateDoc(doc(db, 'profiles', user.uid), {
                has_manual_song_order: true,
                updated_at: serverTimestamp(),
            });
            
            // Show success message
            toast({ 
                title: "Success!", 
                description: "Song order updated successfully." 
            });
        } catch (error) {
            console.error("Error updating song order:", error);
            toast({ 
                title: "Error", 
                description: "Could not save new order. Please try again.", 
                variant: "destructive" 
            });
            // Revert to original order on error
            await loadSongsAndTags();
        }
    };

    // ✅ FREE MUSIC APIs FOR ENHANCED MATCHING
    const searchMusicBrainz = async (query) => {
        try {
            const response = await fetch(`https://musicbrainz.org/ws/2/recording/?query=${encodeURIComponent(query)}&fmt=json&limit=5`);
            if (!response.ok) throw new Error('MusicBrainz request failed');
            
            const data = await response.json();
            return data.recordings?.map(recording => ({
                title: recording.title,
                artist: recording['artist-credit']?.[0]?.name || 'Unknown Artist',
                album: recording.releases?.[0]?.title || '',
                duration: recording.length ? Math.round(recording.length / 1000) : 0,
                source: 'musicbrainz',
                confidence: 'api_match'
            })) || [];
        } catch (error) {
            console.log('MusicBrainz search failed:', error);
            return [];
        }
    };

    const searchiTunes = async (query) => {
        try {
            const response = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(query)}&entity=song&limit=5`);
            if (!response.ok) throw new Error('iTunes request failed');
            
            const data = await response.json();
            return data.results?.map(track => ({
                title: track.trackName,
                artist: track.artistName,
                album: track.collectionName || '',
                duration: Math.round(track.trackTimeMillis / 1000),
                genre: track.primaryGenreName || '',
                artwork: track.artworkUrl100 || '',
                source: 'itunes',
                confidence: 'api_match'
            })) || [];
        } catch (error) {
            console.log('iTunes search failed:', error);
            return [];
        }
    };

    // ✅ ENHANCED SONG MATCHING WITH FREE APIs
    const enhancedSongMatching = async (filename) => {
        const cleanFilename = filename.toLowerCase()
            .replace(/\.(mp3|wav|m4a|flac|aac|ogg)$/i, '') // Remove file extension
            .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
            .trim();

        const results = [];
        
        // 1. Try MusicBrainz API (comprehensive database)
        try {
            const mbResults = await searchMusicBrainz(cleanFilename);
            results.push(...mbResults);
        } catch (error) {
            console.log('MusicBrainz search failed:', error);
        }
        
        // 2. Try iTunes API (clean, reliable data)
        try {
            const itunesResults = await searchiTunes(cleanFilename);
            results.push(...itunesResults);
        } catch (error) {
            console.log('iTunes search failed:', error);
        }
        
        // 3. Deduplicate and rank results
        return deduplicateAndRank(results);
    };

    // ✅ DEDUPLICATE AND RANK RESULTS
    const deduplicateAndRank = (results) => {
        const seen = new Set();
        const uniqueResults = [];
        
        for (const result of results) {
            const key = `${result.title.toLowerCase()}-${result.artist.toLowerCase()}`;
            if (!seen.has(key)) {
                seen.add(key);
                uniqueResults.push(result);
            }
        }
        
        // Rank by source preference: iTunes > MusicBrainz > Local
        const sourceRank = { 'itunes': 3, 'musicbrainz': 2, 'local': 1 };
        uniqueResults.sort((a, b) => {
            const rankA = sourceRank[a.source] || 0;
            const rankB = sourceRank[b.source] || 0;
            return rankB - rankA;
        });
        
        return uniqueResults;
    };

    // ✅ RAPID SONG UPLOADER FUNCTIONS
    const openAddOptions = () => {
        setShowAddOptions(true);
    };

    const handleMobileAddSong = async (songsToAdd) => {
        // Handle adding songs from mobile interface
        if (!user) return;

        try {
            // Validate all songs have valid prices before saving
            const songsToValidate = Array.isArray(songsToAdd) ? songsToAdd : [songsToAdd];
            const invalidSongs = [];
            
            for (const songData of songsToValidate) {
                const price = parseFloat(songData.price) || 0;
                const isTestSong = songData.title.trim().toLowerCase() === 'test' && 
                                  songData.original_artist.trim().toLowerCase() === 'test';
                
                if (!isTestSong && (isNaN(price) || price < minimumPrice)) {
                    invalidSongs.push({
                        title: songData.title.trim(),
                        artist: songData.original_artist.trim(),
                        price: price
                    });
                } else if (isTestSong && (isNaN(price) || price < testSongMinimum)) {
                    invalidSongs.push({
                        title: songData.title.trim(),
                        artist: songData.original_artist.trim(),
                        price: price,
                        isTest: true
                    });
                }
            }

            if (invalidSongs.length > 0) {
                const songDetails = invalidSongs.map(s => 
                    `"${s.title}" by ${s.artist} - Currently ${format(s.price)}, needs to be at least ${s.isTest ? format(testSongMinimum) : formattedMinimum}`
                ).join('\n');

                toast({
                    title: "⚠️ Price Too Low - Cannot Save",
                    description: `Some songs are priced below the minimum requirement of ${formattedMinimum} per song.\n\n${songDetails}\n\nPlease update all song prices to at least ${formattedMinimum} to continue. We recommend starting at ${format(minimumPrice)}-${format(minimumPrice + 1)} per request for better earnings.`,
                    variant: "destructive",
                    duration: 10000
                });
                return;
            }

            if (Array.isArray(songsToAdd)) {
                // Multiple songs added
                for (const songData of songsToAdd) {
                    const songToSave = {
                        user_id: user.uid,
                        title: songData.title.trim(),
                        original_artist: songData.original_artist.trim(),
                        price: parseFloat(songData.price) || 0,
                        order: songs.length,
                        created_at: serverTimestamp()
                    };
                    await addDoc(collection(db, 'songs'), songToSave);
                }
                toast({ title: "Success!", description: `Added ${songsToAdd.length} song${songsToAdd.length > 1 ? 's' : ''} to your catalogue.` });
                
                // Update referral signup stage if this is a referred user
                await updateReferralSignupStage(user.uid);
            } else {
                // Single song added
                const songToSave = {
                    user_id: user.uid,
                    title: songsToAdd.title.trim(),
                    original_artist: songsToAdd.original_artist.trim(),
                    price: parseFloat(songsToAdd.price) || 0,
                    order: songs.length,
                    created_at: serverTimestamp()
                };
                await addDoc(collection(db, 'songs'), songToSave);
                toast({ title: "Success!", description: "Song added successfully." });
                
                // Update referral signup stage if this is a referred user
                await updateReferralSignupStage(user.uid);
            }
            
            await loadSongsAndTags();
        } catch (error) {
            console.error("Error adding songs:", error);
            toast({ title: "Error", description: "Failed to add songs.", variant: "destructive" });
        }
    };

    const closeAddOptions = () => {
        setShowAddOptions(false);
        setUploadedFiles([]);
    };

    const handleSongWizardComplete = () => {
        setShowSongWizard(false);
        // Reload songs after wizard completion
        loadSongsAndTags();
    };

    const openManualDialog = () => {
        setShowAddOptions(false);
        setMultiSongMode(true);
        setAddedSongs([]);
        setFormData({ title: '', original_artist: '', price: '' });
        setIsDialogOpen(true);
    };

    const openRapidUploader = () => {
        setShowAddOptions(false);
        setShowRapidUploader(true);
    };

    // Drag and drop handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        const audioFiles = files.filter(file => 
            file.type.startsWith('audio/') || 
            file.name.toLowerCase().endsWith('.mp3') ||
            file.name.toLowerCase().endsWith('.wav') ||
            file.name.toLowerCase().endsWith('.m4a') ||
            file.name.toLowerCase().endsWith('.flac')
        );

        if (audioFiles.length === 0) {
            toast({ title: "Error", description: "Please drop audio files only (MP3, WAV, M4A, FLAC).", variant: "destructive" });
            return;
        }

        processAudioFiles(audioFiles);
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        processAudioFiles(files);
    };

    // Process audio files and extract metadata
    const processAudioFiles = async (files) => {
        const processedFiles = [];

        for (const file of files) {
            try {
                const metadata = await extractAudioMetadata(file);
                processedFiles.push({
                    file,
                    metadata,
                    id: Math.random().toString(36).substr(2, 9)
                });
            } catch (error) {
                console.error('Error processing file:', file.name, error);
                // Add file with basic info if metadata extraction fails
                processedFiles.push({
                    file,
                    metadata: {
                        title: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
                        artist: 'Unknown Artist',
                        album: '',
                        duration: 0,
                        confidence: 'error',
                        matched: false
                    },
                    id: Math.random().toString(36).substr(2, 9)
                });
            }
        }

        setUploadedFiles(prev => [...prev, ...processedFiles]);
    };

    const removeFile = (fileId) => {
        setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    };

    const saveEdit = (fileId, field, value) => {
        setUploadedFiles(prev => prev.map(file => 
            file.id === fileId 
                ? { ...file, metadata: { ...file.metadata, [field]: value } }
                : file
        ));
    };

    // ✅ DEBUG: Add function to clear setlist wizard flag for testing
    React.useEffect(() => {
        // Make debug function available globally for testing
        window.clearSetlistWizardFlag = () => {
            localStorage.removeItem('ysr_setlist_wizard_seen');
            console.log('🔍 DEBUG: Cleared ysr_setlist_wizard_seen flag');
        };
        window.checkSetlistWizardFlag = () => {
            const flag = localStorage.getItem('ysr_setlist_wizard_seen');
            console.log('🔍 DEBUG: ysr_setlist_wizard_seen flag:', flag);
            return flag;
        };
        window.forceShowSetlistWizard = () => {
            console.log('🔍 DEBUG: Forcing SetlistWizard to show');
            setShowSetlistWizard(true);
        };
    }, []);

    // ✅ Setlist Management Functions
    const openCreateSetlist = () => {
        console.log('🔍 openCreateSetlist called');
        console.log('🔍 Current setlists length:', setlists.length);
        console.log('🔍 Current setlists:', setlists);
        
        // Check if user has setlists
        
        // Show wizard whenever user has no setlists (regardless of whether they've seen it before)
        if (setlists.length === 0) {
            console.log('🎯 Showing SetlistWizard (no setlists exist)');
            setShowSetlistWizard(true);
        } else {
            console.log('🎯 Showing normal create setlist dialog');
            console.log('🔍 Reason - setlists.length:', setlists.length);
            setEditingSetlist(null);
            setSetlistFormData({ name: '', selectedSongs: [] });
            setShowCreateSetlist(true);
        }
    };

    const openEditSetlist = (setlist) => {
        setEditingSetlist(setlist);
        setSetlistFormData({
            name: setlist.name || '',
            selectedSongs: setlist.songs || []
        });
        setShowCreateSetlist(true);
    };

    const handleWizardComplete = () => {
        console.log('🔍 handleWizardComplete called');
        // Don't set the seen flag - we want the wizard to show again if they delete all setlists
        
        setShowSetlistWizard(false);
        setEditingSetlist(null);
        setSetlistFormData({ name: '', selectedSongs: [] });
        setShowCreateSetlist(true);
        console.log('🔍 Set showCreateSetlist to true');
    };

    const closeSetlistDialog = () => {
        setShowCreateSetlist(false);
        setEditingSetlist(null);
        setSetlistFormData({ name: '', selectedSongs: [] });
    };

    const handleSetlistInputChange = (e) => {
        const { value } = e.target;
        setSetlistFormData(prev => ({ ...prev, name: value }));
    };

    const toggleSongInSetlist = (songId) => {
        setSetlistFormData(prev => ({
            ...prev,
            selectedSongs: prev.selectedSongs.includes(songId)
                ? prev.selectedSongs.filter(id => id !== songId)
                : [...prev.selectedSongs, songId]
        }));
    };

    const handleSetlistSubmit = async (e) => {
        e.preventDefault();
        if (!user) return;

        try {
            const setlistData = {
                user_id: user.uid,
                name: setlistFormData.name.trim(),
                songs: setlistFormData.selectedSongs,
                created_at: serverTimestamp()
            };

            if (editingSetlist) {
                await updateDoc(doc(db, 'playlists', editingSetlist.id), setlistData);
                if (!mobile.isMobile) {
                    toast({ title: "Success!", description: "Setlist updated successfully." });
                }
            } else {
                await addDoc(collection(db, 'playlists'), setlistData);
                if (!mobile.isMobile) {
                    toast({ title: "Success!", description: "Setlist created successfully." });
                }
            }

            closeSetlistDialog();
            await loadSetlists();
        } catch (error) {
            console.error("Error saving setlist:", error);
            toast({ title: "Error", description: "Failed to save setlist.", variant: "destructive" });
        }
    };

    const deleteSetlist = async (setlistId) => {
        if (!user) return;

        try {
            await deleteDoc(doc(db, 'playlists', setlistId));
            if (!mobile.isMobile) {
                toast({ title: "Success!", description: "Setlist deleted successfully." });
            }
            await loadSetlists();
        } catch (error) {
            console.error("Error deleting setlist:", error);
            toast({ title: "Error", description: "Failed to delete setlist.", variant: "destructive" });
        }
    };

    const handleRapidUpload = async () => {
        if (uploadedFiles.length === 0) {
            toast({ title: "Error", description: "Please upload some audio files first.", variant: "destructive" });
            return;
        }

        setIsUploadingRapid(true);
        try {
            // Get the next order number
            const nextOrder = songs.length;

            // Add songs in batch
            const batch = writeBatch(db);
            const addedSongs = [];

            uploadedFiles.forEach((fileData, index) => {
                const songRef = doc(collection(db, 'songs'));
                const songDoc = {
                    user_id: user.uid,
                    title: fileData.metadata.title,
                    original_artist: fileData.metadata.artist,
                    price: 5.00, // Default price

                    order: nextOrder + index,
                    created_at: serverTimestamp(),
                    duration: fileData.metadata.duration,
                    filename: fileData.file.name,
                    confidence: fileData.metadata.confidence,
                    matched: fileData.metadata.matched
                };
                batch.set(songRef, songDoc);
                addedSongs.push({ id: songRef.id, ...songDoc });
            });

            await batch.commit();

            toast({ 
                title: "Success!", 
                description: `Added ${uploadedFiles.length} song${uploadedFiles.length > 1 ? 's' : ''} to your catalogue.` 
            });

            // Refresh the songs list
            await loadSongsAndTags();
            
            // Close the dialog and reset
            setShowRapidUploader(false);
            setUploadedFiles([]);
        } catch (error) {
            console.error("Error uploading rapid songs:", error);
            toast({ title: "Error", description: "Failed to upload songs. Please try again.", variant: "destructive" });
        } finally {
            setIsUploadingRapid(false);
        }
    };

    // ✅ ENHANCED METADATA EXTRACTION WITH FREE APIs
    const extractAudioMetadata = async (file) => {
        return new Promise(async (resolve, reject) => {
            const audio = new Audio();
            const url = URL.createObjectURL(file);

            audio.addEventListener('loadedmetadata', async () => {
                URL.revokeObjectURL(url);
                
                try {
                    // Try to find a match using our enhanced matching with free APIs
                    const matches = await enhancedSongMatching(file.name);
                    
                    if (matches.length > 0) {
                        const bestMatch = matches[0];
                        resolve({
                            title: bestMatch.title,
                            artist: bestMatch.artist,
                            album: bestMatch.album || '',
                            duration: Math.round(audio.duration) || 0,
                            confidence: bestMatch.confidence,
                            matched: true,
                            source: bestMatch.source,
                            genre: bestMatch.genre || '',
                            artwork: bestMatch.artwork || ''
                        });
                    } else {
                        // Fallback to filename parsing
                        const fileName = file.name.replace(/\.[^/.]+$/, "");
                        let title = fileName;
                        let artist = 'Unknown Artist';

                        // Try to parse "Artist - Title" or "Title - Artist" from filename
                        const dashIndex = fileName.lastIndexOf(' - ');
                        if (dashIndex !== -1) {
                            const part1 = fileName.substring(0, dashIndex).trim();
                            const part2 = fileName.substring(dashIndex + 3).trim();
                            
                            // Assume the longer part is the title
                            if (part1.length > part2.length) {
                                title = part1;
                                artist = part2;
                            } else {
                                title = part2;
                                artist = part1;
                            }
                        }

                        resolve({
                            title: capitalizeWords(title),
                            artist: capitalizeWords(artist),
                            album: '',
                            duration: Math.round(audio.duration) || 0,
                            confidence: 'filename_parsed',
                            matched: false,
                            source: 'local'
                        });
                    }
                } catch (error) {
                    console.error('Error in enhanced matching:', error);
                    // Fallback to basic filename parsing
                    const fileName = file.name.replace(/\.[^/.]+$/, "");
                    resolve({
                        title: capitalizeWords(fileName),
                        artist: 'Unknown Artist',
                        album: '',
                        duration: Math.round(audio.duration) || 0,
                        confidence: 'filename_parsed',
                        matched: false,
                        source: 'local'
                    });
                }
            });

            audio.addEventListener('error', () => {
                URL.revokeObjectURL(url);
                reject(new Error('Failed to load audio file'));
            });

            audio.src = url;
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Setlist Management | YourSongRequest</title>
                <meta name="description" content="Manage your song catalogue and setlist for live performances" />
            </Helmet>
            
            {/* Mobile-optimized Catalogue */}
            {mobile.isMobile && (
                <MobileCatalogue
                    songs={songs}
                    archivedSongs={archivedSongs}
                    playlists={setlists}
                    activePlaylist={null}
                    catalogueToolsSlot={
                        songs.length > 0 ? (
                            <BatchSongPricesButton
                                songs={songs}
                                currency={currency}
                                format={format}
                                onApplied={loadSongsAndTags}
                            />
                        ) : null
                    }
                    catalogueTopSlot={
                        user?.uid && songs.length > 0 ? (
                            <PopularSongsCatalogueSettings
                                userId={user.uid}
                                profile={profile}
                                songs={songs}
                                refreshProfile={refreshProfile}
                                className="mb-4"
                            />
                        ) : null
                    }
                    onAddSong={handleMobileAddSong}
                    onEditSong={(song) => {
                        setEditingSong(song);
                        setFormData({ title: song.title || '', original_artist: song.original_artist || '', price: song.price || '' });
                        setIsDialogOpen(true);
                    }}
                    onDeleteSong={deleteSong}
                    onRestoreSong={restoreSong}
                    onAddPlaylist={openCreateSetlist}
                    onEditPlaylist={openEditSetlist}
                    onSelectPlaylist={(playlist) => {
                        // Handle playlist selection
                        console.log('Selected playlist:', playlist);
                    }}
                    onMarkAsPlayed={(song) => {
                        // Handle mark as played
                        console.log('Mark as played:', song);
                    }}
                />
            )}
            
            {/* Desktop Catalogue */}
            {!mobile.isMobile && (
                <div className="container mx-auto px-4 py-8">
                <div className="flex items-center justify-between mb-8">
                        <div>
                        <h1 className="text-3xl font-bold text-white mb-2">Setlist Management</h1>
                        <p className="text-gray-400">Manage your song catalogue and organize your setlists</p>
                        </div>
                    <div className="flex items-center space-x-3">
                        {activeTab === 'songs' && (
                            <Button onClick={() => {
                                console.log('🔍 SetlistPage: Add Song button clicked');
                                openAddSongEntry();
                            }} className="bg-blue-600 hover:bg-blue-700">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Song
                        </Button>
                        )}
                        {activeTab === 'setlists' && (
                            <Button onClick={openCreateSetlist} className="bg-orange-600 hover:bg-orange-700">
                                <Plus className="w-4 h-4 mr-2" />
                                Create Setlist
                            </Button>
                        )}
                    </div>
                    </div>

                {/* Tab Navigation */}
                <div className="flex space-x-1 mb-6 bg-gray-800 p-1 rounded-lg">
                    <button
                        onClick={() => {
                            setActiveTab('songs');
                            setSearchParams({});
                        }}
                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                            activeTab === 'songs'
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-400 hover:text-white hover:bg-gray-700'
                        }`}
                    >
                        <Music className="w-4 h-4 inline mr-2" />
                        Song Catalogue
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('setlists');
                            setSearchParams({ tab: 'setlists' });
                        }}
                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                            activeTab === 'setlists'
                                ? 'bg-orange-600 text-white'
                                : 'text-gray-400 hover:text-white hover:bg-gray-700'
                        }`}
                    >
                        <ListMusic className="w-4 h-4 inline mr-2" />
                        Setlists
                    </button>
                                </div>

                {/* Content based on active tab */}
                {activeTab === 'songs' && (
                    <>
                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <Card className="bg-gray-800 border-gray-700">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-400 text-sm">Active Songs</p>
                                    <p className="text-2xl font-bold text-white">{songs.length}</p>
                                </div>
                                <Music className="w-8 h-8 text-blue-400" />
                                </div>
                            </CardContent>
                        </Card>
                    <Card className="bg-gray-800 border-gray-700">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-400 text-sm">Archived Songs</p>
                                    <p className="text-2xl font-bold text-white">{archivedSongs.length}</p>
                                </div>
                                <Archive className="w-8 h-8 text-gray-400" />
                                </div>
                            </CardContent>
                        </Card>

                    </div>

                    {user?.uid && songs.length > 0 && (
                        <PopularSongsCatalogueSettings
                            userId={user.uid}
                            profile={profile}
                            songs={songs}
                            refreshProfile={refreshProfile}
                            className="mb-8"
                        />
                    )}

                {/* Search and Toggle Archived */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-6">
                    <div className="flex-1 min-w-[140px]">
                        <Input
                            type="text"
                            placeholder="Search by song title or artist name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                        />
                    </div>
                    {songs.length > 0 && (
                        <BatchSongPricesButton
                            songs={songs}
                            currency={currency}
                            format={format}
                            onApplied={loadSongsAndTags}
                        />
                    )}
                    <Button
                        variant={showArchived ? "outline" : "default"}
                        onClick={() => setShowArchived(!showArchived)}
                        className={showArchived ? "border-gray-600 text-gray-300" : "bg-blue-600 hover:bg-blue-700 shrink-0"}
                    >
                        {showArchived ? <Archive className="w-4 h-4 mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                        {showArchived ? 'Show Active Songs' : 'Show Archived Songs'}
                    </Button>
                </div>

                    {/* Songs List */}
                    {!showArchived ? (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="active-songs">
                                {(provided) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    className="space-y-3"
                                >
                                        {(() => {
                                            // Filter songs by search query (searches title, artist, and original_artist)
                                            const filteredSongs = songs.filter(song => {
                                                if (!searchQuery.trim()) return true;
                                                const query = searchQuery.toLowerCase();
                                                const title = (song.title || '').toLowerCase();
                                                const artist = (song.artist || '').toLowerCase();
                                                const originalArtist = (song.original_artist || '').toLowerCase();
                                                return title.includes(query) || artist.includes(query) || originalArtist.includes(query);
                                            });
                                            
                                            if (filteredSongs.length === 0) {
                                                return (
                                                    <div className="text-center py-8">
                                                        <p className="text-gray-400">
                                                            {searchQuery.trim() 
                                                                ? `No songs found matching "${searchQuery}"` 
                                                                : 'No songs in your catalogue'}
                                                        </p>
                                                    </div>
                                                );
                                            }
                                            
                                            return filteredSongs.map((song) => {
                                                // Find original index for drag and drop
                                                const originalIndex = songs.findIndex(s => s.id === song.id);
                                                return (
                                            <Draggable key={song.id} draggableId={song.id} index={originalIndex}>
                                                {(provided, snapshot) => (
                                                <motion.div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                >
                                                    <Card className={`bg-gray-800 border-gray-700 ${snapshot.isDragging ? 'shadow-lg' : ''}`}>
                                                        <CardContent className="p-4">
                                                        <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-4 flex-1 min-w-0">
                                                                    <div
                                                                        {...provided.dragHandleProps}
                                                                        className="cursor-grab active:cursor-grabbing"
                                                                    >
                                                                    <GripVertical className="w-5 h-5 text-gray-400" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-start justify-between gap-2">
                                                                        <h3 className="text-lg font-semibold text-white truncate min-w-0">
                                                                            {capitalizeWords(song.title)}
                                                                        </h3>
                                                                        <span className="text-xs font-semibold text-purple-300 tabular-nums shrink-0 whitespace-nowrap leading-tight pt-0.5">
                                                                            {format(parseFloat(song.price) || 0)}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-gray-400">by {capitalizeWords(song.original_artist || song.artist || 'Unknown Artist')}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center space-x-2 ml-4">
                                                                {!showArchived && (
                                                                    <>
                                                                <Button
                                                                    size="sm"
                                                                            variant="outline"
                                                                    onClick={() => openDialog(song)}
                                                                            className="text-blue-400 hover:text-blue-300 border-blue-600"
                                                                >
                                                                    <Edit className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                            variant="outline"
                                                                            onClick={() => archiveSong(song.id)}
                                                                            className="text-gray-400 hover:text-gray-300 border-gray-600"
                                                                        >
                                                                            <Archive className="w-4 h-4" />
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            onClick={() => deleteSong(song.id)}
                                                                            className="text-red-400 hover:text-red-300 border-red-600"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                                    </>
                                                                )}
                                                                {showArchived && (
                                                                    <>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            onClick={() => restoreSong(song.id)}
                                                                            className="text-green-400 hover:text-green-300 border-green-600"
                                                                        >
                                                                            <RotateCcw className="w-4 h-4" />
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            onClick={() => deleteSong(song.id)}
                                                                            className="text-red-400 hover:text-red-300 border-red-600"
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            </motion.div>
                                                        )}
                                                    </Draggable>
                                                );
                                            });
                                        })()}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    ) : (
                    /* Archived Songs List - No Drag and Drop */
                    <div className="space-y-3">
                        {archivedSongs.map((song, index) => (
                            <motion.div
                                key={song.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Card className="bg-gray-800 border-gray-700">
                                    <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-4 flex-1 min-w-0">
                                                <div className="w-5 h-5 text-gray-500">
                                                    <Archive className="w-5 h-5" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <h3 className="text-lg font-semibold text-white truncate min-w-0">
                                                            {capitalizeWords(song.title)}
                                                        </h3>
                                                        <span className="text-xs font-semibold text-purple-300 tabular-nums shrink-0 whitespace-nowrap leading-tight pt-0.5">
                                                            {format(parseFloat(song.price) || 0)}
                                                        </span>
                                                    </div>
                                            <p className="text-gray-400">by {capitalizeWords(song.original_artist || song.artist || 'Unknown Artist')}</p>
                                        </div>
                                            </div>
                                            <div className="flex items-center space-x-2 ml-4">
                                            <Button
                                                size="sm"
                                                    variant="outline"
                                                    onClick={() => restoreSong(song.id)}
                                                    className="text-green-400 hover:text-green-300 border-green-600"
                                            >
                                                <RotateCcw className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                size="sm"
                                                    variant="outline"
                                                    onClick={() => deleteSong(song.id)}
                                                    className="text-red-400 hover:text-red-300 border-red-600"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                            ))}
                        </div>
                    )}

                {/* Empty State */}
                {(showArchived ? archivedSongs : songs).length === 0 && (
                    <Card className="bg-gray-800 border-gray-700">
                            <CardContent className="p-8 text-center">
                            <Music className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-white mb-2">
                                {showArchived ? 'No Archived Songs' : 'No Songs Yet'}
                            </h3>
                            <p className="text-gray-400 mb-4">
                                {showArchived 
                                    ? 'You haven\'t archived any songs yet.' 
                                    : 'Start building your song catalogue by adding your first song.'
                                }
                            </p>
                            {!showArchived && (
                                <Button onClick={() => {
                                    console.log('🔍 SetlistPage: Add Your First Song button clicked');
                                    openAddSongEntry();
                                }} className="bg-blue-600 hover:bg-blue-700">
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add Your First Song
                                </Button>
                            )}
                            </CardContent>
                        </Card>
                    )}
                    </>
                )}

                {/* Setlists Tab Content */}
                {activeTab === 'setlists' && (
                    <>
                        {/* Setlists Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                            <Card className="bg-gray-800 border-gray-700">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-gray-400 text-sm">Total Setlists</p>
                                            <p className="text-2xl font-bold text-white">{setlists.length}</p>
                                        </div>
                                        <ListMusic className="w-8 h-8 text-green-400" />
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="bg-gray-800 border-gray-700">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-gray-400 text-sm">Total Songs</p>
                                            <p className="text-2xl font-bold text-white">{songs.length}</p>
                                        </div>
                                        <Music className="w-8 h-8 text-blue-400" />
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="bg-gray-800 border-gray-700">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-gray-400 text-sm">Average Songs/Setlist</p>
                                            <p className="text-2xl font-bold text-white">
                                                {setlists.length > 0 
                                                    ? Math.round(setlists.reduce((acc, setlist) => acc + (setlist.songs?.length || 0), 0) / setlists.length)
                                                    : 0
                                                }
                                            </p>
                                        </div>
                                        <CheckCircle className="w-8 h-8 text-purple-400" />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Setlists List */}
                        <div className="space-y-4">
                            {setlists.length === 0 ? (
                                <Card className="bg-gray-800 border-gray-700">
                            <CardContent className="p-8 text-center">
                                        <ListMusic className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-xl font-semibold text-white mb-2">No Setlists Yet</h3>
                                        <p className="text-gray-400 mb-4">
                                            Create your first setlist to organize songs for different performances.
                                        </p>
                                        <Button onClick={openCreateSetlist} className="bg-green-600 hover:bg-green-700">
                                            <Plus className="w-4 h-4 mr-2" />
                                            Create Your First Setlist
                                        </Button>
                            </CardContent>
                        </Card>
                            ) : (
                                setlists.map((setlist) => (
                                    <Card key={setlist.id} className="bg-gray-800 border-gray-700">
                                        <CardContent className="p-6">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-3 mb-2">
                                                        <ListMusic className="w-5 h-5 text-green-400" />
                                                        <h3 className="text-lg font-semibold text-white">{setlist.name}</h3>
                                                        <span className="px-2 py-1 bg-green-600/20 text-green-300 text-xs rounded-full">
                                                            {setlist.songs?.length || 0} songs
                                                        </span>
                                                    </div>
                                                    {setlist.description && (
                                                        <p className="text-gray-400 text-sm mb-3">{setlist.description}</p>
                                                    )}
                                                    <div className="flex flex-wrap gap-1">
                                                        {setlist.songs?.slice(0, 5).map((songId) => {
                                                            const song = songs.find(s => s.id === songId);
                                                            return song ? (
                                                                <span
                                                                    key={songId}
                                                                    className="px-2 py-1 bg-blue-600/20 text-blue-300 text-xs rounded-full"
                                                                >
                                                                    {song.title}
                                                                </span>
                                                            ) : null;
                                                        })}
                                                        {setlist.songs?.length > 5 && (
                                                            <span className="px-2 py-1 bg-gray-600/20 text-gray-300 text-xs rounded-full">
                                                                +{setlist.songs.length - 5} more
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-2 ml-4">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => openEditSetlist(setlist)}
                                                        className="text-blue-400 hover:text-blue-300 border-blue-600"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => deleteSetlist(setlist.id)}
                                                        className="text-red-400 hover:text-red-300 border-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))
                            )}
                        </div>
                    </>
                )}
            </div>
            )}

            {/* Add Options Dialog */}
            <Dialog open={showAddOptions} onOpenChange={setShowAddOptions}>
                <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add Songs</DialogTitle>
                        <DialogDescription>
                            Choose how you want to add songs to your catalogue
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Button
                                onClick={openManualDialog}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Song Manually
                            </Button>
                            <p className="text-gray-400 text-sm">
                                Manually enter song details like title, artist, and duration. Perfect for songs you know well or want to customize.
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Button
                                onClick={openRapidUploader}
                                className="w-full bg-green-600 hover:bg-green-700 text-white"
                            >
                                <Music className="w-4 h-4 mr-2" />
                                Rapid Song Uploader
                            </Button>
                            <p className="text-gray-400 text-sm">
                                Upload audio files and automatically extract song metadata. Great for bulk uploads and songs with embedded information.
                            </p>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Rapid Song Uploader Dialog */}
            <Dialog open={showRapidUploader} onOpenChange={setShowRapidUploader}>
                <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Rapid Song Uploader</DialogTitle>
                        <DialogDescription>
                            Drag and drop your audio files to automatically extract song metadata
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto space-y-6 pr-2">
                        {/* Drag and Drop Zone */}
                        <div
                            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                                isDragging
                                    ? 'border-blue-400 bg-blue-900/20'
                                    : 'border-gray-600 bg-gray-800/20'
                            }`}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                        >
                            <Music className="w-8 h-8 mx-auto mb-3 text-gray-400" />
                            <h3 className="text-base font-medium text-white mb-2">
                                {isDragging ? 'Drop your audio files here' : 'Drag and drop audio files here'}
                            </h3>
                            <p className="text-gray-400 mb-3 text-sm">
                                Supports MP3, WAV, M4A, FLAC files
                            </p>
                            <div className="flex items-center justify-center space-x-4">
                                <span className="text-gray-500 text-sm">or</span>
                                <label className="cursor-pointer">
                                    <input
                                        type="file"
                                        multiple
                                        accept="audio/*,.mp3,.wav,.m4a,.flac"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                    />
                                    <span className="text-blue-400 hover:text-blue-300 underline text-sm">
                                        Browse files
                                    </span>
                                </label>
                            </div>
                        </div>

                        {/* Uploaded Files List */}
                        {uploadedFiles.length > 0 && (
                            <div className="space-y-3">
                                <h4 className="text-white font-medium">Uploaded Files ({uploadedFiles.length})</h4>
                                <div className="max-h-64 overflow-y-auto space-y-2">
                                    {uploadedFiles.map((fileData) => (
                                        <div key={fileData.id} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-3">
                                                    <Music className="w-4 h-4 text-blue-400" />
                                                    <div className="flex-1">
                                                        {/* Always Editable Title */}
                                                        <Input
                                                            value={fileData.metadata.title}
                                                            onChange={(e) => saveEdit(fileData.id, 'title', e.target.value)}
                                                            className="bg-gray-700 border-gray-600 text-white text-sm mb-1"
                                                            placeholder="Song title"
                                                        />
                                                        
                                                        {/* Always Editable Artist */}
                                                        <Input
                                                            value={fileData.metadata.artist}
                                                            onChange={(e) => saveEdit(fileData.id, 'artist', e.target.value)}
                                                            className="bg-gray-700 border-gray-600 text-white text-sm mb-1"
                                                            placeholder="Artist name"
                                                        />
                                                        
                                                        {fileData.metadata.duration > 0 && (
                                                            <p className="text-gray-500 text-xs">
                                                                {Math.floor(fileData.metadata.duration / 60)}:
                                                                {(fileData.metadata.duration % 60).toString().padStart(2, '0')}
                                                            </p>
                                                        )}
                                                        
                                                        {/* Match Quality Indicator */}
                                                        <div className="flex items-center space-x-1 mt-1">
                                                            <span className={`text-xs px-2 py-1 rounded-full ${
                                                                fileData.metadata.matched ? 'bg-green-900/50 text-green-300' : 'bg-yellow-900/50 text-yellow-300'
                                                            }`}>
                                                                {fileData.metadata.confidence === 'api_match' ? '✓ Matched' :
                                                                 fileData.metadata.confidence === 'filename_parsed' ? '📝 Parsed' :
                                                                 fileData.metadata.confidence === 'error' ? '❌ Error' :
                                                                 '❓ Unknown'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <p className="text-gray-500 text-xs mt-1">{fileData.file.name}</p>
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => removeFile(fileData.id)}
                                                className="text-red-400 hover:text-red-300 border-red-600"
                                            >
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Info Box - Only show when no files uploaded */}
                        {uploadedFiles.length === 0 && (
                            <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                                <h4 className="text-blue-300 font-medium mb-2">How it works:</h4>
                                <ul className="text-blue-200 text-sm space-y-1">
                                    <li>• Drag and drop your audio files into the zone above</li>
                                    <li>• Intelligent matching against music databases</li>
                                    <li>• Supports "Artist - Title" and "Title - Artist" filename formats</li>
                                    <li>• Edit song title and artist directly in the text boxes</li>
                                    <li>• Default price: {format(5)} per song</li>
                                    <li>• You can edit prices after uploading</li>
                                    <li>• Files are processed locally - no files are uploaded to our servers</li>
                                </ul>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="mt-6">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setShowRapidUploader(false);
                                setUploadedFiles([]);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleRapidUpload}
                            disabled={isUploadingRapid || uploadedFiles.length === 0}
                            className="bg-green-600 hover:bg-green-700"
                        >
                            {isUploadingRapid ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Adding Songs...
                                </>
                            ) : (
                                <>
                                    <Music className="w-4 h-4 mr-2" />
                                    Add {uploadedFiles.length} Song{uploadedFiles.length !== 1 ? 's' : ''} to Catalogue
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create/Edit Setlist Modal - Mobile Optimized */}
            {mobile.isMobile ? (
                <MobileAnimatedModal
                    isOpen={showCreateSetlist}
                    onClose={() => setShowCreateSetlist(false)}
                    title={editingSetlist ? 'Edit Setlist' : 'Create New Setlist'}
                    icon={ListMusic}
                    position="center"
                    maxWidth="lg"
                    className="max-h-[90vh]"
                >
                    <form onSubmit={handleSetlistSubmit} className="space-y-4" autoComplete="off">
                        {/* Hidden fields to prevent autofill */}
                        <input type="text" style={{ display: 'none' }} autoComplete="off" />
                        <input type="password" style={{ display: 'none' }} autoComplete="off" />
                        {mobile.isMobile ? (
                            // Mobile-optimized layout - Single scroll area
                            <div className="space-y-4">
                                {/* Form Section - Fixed at top */}
                                <div className="space-y-3">
                                    <div className="space-y-2">
                                        <Label htmlFor="setlist-name" className="text-base font-medium">Setlist Name *</Label>
                                        <Input
                                            id="setlist-name"
                                            name="setlist-name-field"
                                            value={setlistFormData.name}
                                            onChange={handleSetlistInputChange}
                                            placeholder="e.g., Acoustic Set, Rock Classics"
                                            autoComplete="off"
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            spellCheck="false"
                                            required
                                            className="bg-gray-800 border-gray-600 text-white h-14 text-lg"
                                        />
                                    </div>
                                </div>
                                
                                {/* Selected Songs - Compact Summary */}
                                {setlistFormData.selectedSongs.length > 0 && (
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-base font-medium">Selected ({setlistFormData.selectedSongs.length})</Label>
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setSetlistFormData(prev => ({ ...prev, selectedSongs: [] }))}
                                                className="text-sm text-red-400 hover:text-red-300 border-red-600 h-8 px-3"
                                            >
                                                Clear All
                                            </Button>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {setlistFormData.selectedSongs.slice(0, 3).map((songId) => {
                                                const song = songs.find(s => s.id === songId);
                                                return song ? (
                                                    <div key={songId} className="flex items-center bg-green-600/20 border border-green-600/40 rounded-full px-3 py-1">
                                                        <span className="text-white text-sm truncate max-w-[120px]">{song.title}</span>
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => toggleSongInSetlist(songId)}
                                                            className="text-red-400 hover:text-red-300 ml-2 h-4 w-4 p-0"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                ) : null;
                                            })}
                                            {setlistFormData.selectedSongs.length > 3 && (
                                                <div className="flex items-center bg-gray-600 rounded-full px-3 py-1">
                                                    <span className="text-white text-sm">+{setlistFormData.selectedSongs.length - 3} more</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Available Songs - Main List */}
                                <div className="space-y-3">
                                    <Label className="text-base font-medium">Choose Songs ({songs.length} available)</Label>
                                    {songs.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                                            <Music className="w-12 h-12 mb-3 opacity-50" />
                                            <p className="text-base">No songs available</p>
                                            <p className="text-sm opacity-75">Add songs to your catalogue first</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {songs.map((song) => (
                                                <div
                                                    key={song.id}
                                                    className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all duration-200 ${
                                                        setlistFormData.selectedSongs.includes(song.id)
                                                            ? 'bg-green-600/20 border border-green-600/40 shadow-[0_0_8px_rgba(34,197,94,0.4)]'
                                                            : 'bg-gray-700 hover:bg-gray-600 active:bg-gray-500'
                                                    }`}
                                                    onClick={() => toggleSongInSetlist(song.id)}
                                                >
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-white text-base font-medium truncate">{capitalizeWords(song.title)}</p>
                                                        <p className="text-gray-400 text-sm truncate">by {capitalizeWords(song.original_artist || song.artist || 'Unknown Artist')}</p>
                                                    </div>
                                                    <div className="ml-3 flex-shrink-0">
                                                        {setlistFormData.selectedSongs.includes(song.id) ? (
                                                            <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                                                                <Check className="w-5 h-5 text-white" />
                                                            </div>
                                                        ) : (
                                                            <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                                                                <Plus className="w-5 h-5 text-white" />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            // Desktop layout
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="setlist-name">Setlist Name *</Label>
                                        <Input
                                            id="setlist-name"
                                            name="setlist-name-field"
                                            value={setlistFormData.name}
                                            onChange={handleSetlistInputChange}
                                            placeholder="e.g., Acoustic Set, Rock Classics, Wedding Songs"
                                            autoComplete="off"
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            spellCheck="false"
                                            required
                                            className="bg-gray-800 border-gray-600 text-white"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Selected Songs ({setlistFormData.selectedSongs.length})</Label>
                                        <div className="bg-gray-800 border border-gray-600 rounded-md p-3 min-h-[100px] max-h-[200px] overflow-y-auto">
                                            {setlistFormData.selectedSongs.length === 0 ? (
                                                <p className="text-gray-400 text-sm">No songs selected yet</p>
                                            ) : (
                                                <div className="space-y-2">
                                                    {setlistFormData.selectedSongs.map((songId) => {
                                                        const song = songs.find(s => s.id === songId);
                                                        return song ? (
                                                            <div key={songId} className="flex items-center justify-between p-2 bg-gray-700 rounded">
                                                                <div>
                                                                    <p className="text-white text-sm font-medium">{capitalizeWords(song.title)}</p>
                                                                    <p className="text-gray-400 text-xs">by {capitalizeWords(song.original_artist || song.artist || 'Unknown Artist')}</p>
                                                                </div>
                                                                <Button
                                                                    type="button"
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => toggleSongInSetlist(songId)}
                                                                    className="text-red-400 hover:text-red-300 border-red-600"
                                                                >
                                                                    <X className="w-3 h-3" />
                                                                </Button>
                                                            </div>
                                                        ) : null;
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <Label>Available Songs ({songs.length})</Label>
                                    <div className="bg-gray-800 border border-gray-600 rounded-md p-3 max-h-[400px] overflow-y-auto">
                                        {songs.length === 0 ? (
                                            <p className="text-gray-400 text-sm">No songs available. Add some songs to your catalogue first.</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {songs.map((song) => (
                                                    <div
                                                        key={song.id}
                                                        className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                                                            setlistFormData.selectedSongs.includes(song.id)
                                                                ? 'bg-green-600/20 border border-green-600/40'
                                                                : 'bg-gray-700 hover:bg-gray-600'
                                                        }`}
                                                        onClick={() => toggleSongInSetlist(song.id)}
                                                    >
                                                        <div className="flex-1">
                                                            <p className="text-white text-sm font-medium">{capitalizeWords(song.title)}</p>
                                                            <p className="text-gray-400 text-xs">by {capitalizeWords(song.original_artist || song.artist || 'Unknown Artist')}</p>
                                                        </div>
                                                        <div className="ml-2">
                                                            {setlistFormData.selectedSongs.includes(song.id) ? (
                                                                <Check className="w-4 h-4 text-green-400" />
                                                            ) : (
                                                                <Plus className="w-4 h-4 text-gray-400" />
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Mobile Action Buttons */}
                        <div className="flex space-x-3 mt-6">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={closeSetlistDialog}
                                className="flex-1 h-14 text-base border-gray-600 text-gray-300 hover:bg-gray-800"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                onClick={handleSetlistSubmit}
                                disabled={!setlistFormData.name.trim() || setlistFormData.selectedSongs.length === 0}
                                className="flex-1 h-14 text-base bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
                            >
                                {editingSetlist ? 'Update Setlist' : 'Create Setlist'}
                            </Button>
                        </div>
                    </form>
                </MobileAnimatedModal>
            ) : (
                /* Desktop Dialog */
                <Dialog open={showCreateSetlist} onOpenChange={setShowCreateSetlist}>
                    <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                        <DialogHeader className="flex-shrink-0">
                            <DialogTitle className="text-lg">{editingSetlist ? 'Edit Setlist' : 'Create New Setlist'}</DialogTitle>
                            <DialogDescription className="text-sm">
                                {editingSetlist ? 'Update your setlist details and song selection' : 'Create a new setlist by selecting songs from your catalogue'}
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSetlistSubmit} className="flex-1 overflow-y-auto space-y-4" autoComplete="off">
                            {/* Hidden fields to prevent autofill */}
                            <input type="text" style={{ display: 'none' }} autoComplete="off" />
                            <input type="password" style={{ display: 'none' }} autoComplete="off" />
                            
                            {/* Desktop Layout */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Left Column - Form */}
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="setlist-name" className="text-base font-medium">Setlist Name *</Label>
                                        <Input
                                            id="setlist-name"
                                            name="setlist-name-field"
                                            value={setlistFormData.name}
                                            onChange={handleSetlistInputChange}
                                            placeholder="e.g., Acoustic Set, Rock Classics"
                                            autoComplete="off"
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            spellCheck="false"
                                            required
                                            className="bg-gray-800 border-gray-600 text-white"
                                        />
                                    </div>
                                    
                                    {/* Selected Songs Summary */}
                                    {setlistFormData.selectedSongs.length > 0 && (
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label className="text-base font-medium">Selected ({setlistFormData.selectedSongs.length})</Label>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setSetlistFormData(prev => ({ ...prev, selectedSongs: [] }))}
                                                    className="text-sm text-red-400 hover:text-red-300 border-red-600"
                                                >
                                                    Clear All
                                                </Button>
                                            </div>
                                            <div className="max-h-32 overflow-y-auto space-y-1">
                                                {setlistFormData.selectedSongs.map((songId) => {
                                                    const song = songs.find(s => s.id === songId);
                                                    return song ? (
                                                        <div key={songId} className="flex items-center justify-between p-2 bg-green-600/20 border border-green-600/40 rounded">
                                                            <span className="text-white text-sm">{capitalizeWords(song.title)} by {capitalizeWords(song.original_artist || song.artist || 'Unknown Artist')}</span>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => toggleSongInSetlist(songId)}
                                                                className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </Button>
                                                        </div>
                                                    ) : null;
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Right Column - Available Songs */}
                                <div className="space-y-3">
                                    <Label className="text-base font-medium">Choose Songs ({songs.length} available)</Label>
                                    {songs.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                                            <Music className="w-12 h-12 mb-3 opacity-50" />
                                            <p className="text-base">No songs available</p>
                                            <p className="text-sm opacity-75">Add songs to your catalogue first</p>
                                        </div>
                                    ) : (
                                        <div className="max-h-96 overflow-y-auto space-y-2">
                                            {songs.map((song) => (
                                                <div
                                                    key={song.id}
                                                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                                                        setlistFormData.selectedSongs.includes(song.id)
                                                            ? 'bg-green-600/20 border border-green-600/40'
                                                            : 'bg-gray-700 hover:bg-gray-600'
                                                    }`}
                                                    onClick={() => toggleSongInSetlist(song.id)}
                                                >
                                                    <div className="flex-1">
                                                        <div className="text-white font-medium">{capitalizeWords(song.title)}</div>
                                                        <div className="text-gray-400 text-sm">by {capitalizeWords(song.original_artist || song.artist || 'Unknown Artist')}</div>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-green-400 font-medium">{format(song.price || 0)}</span>
                                                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                                            setlistFormData.selectedSongs.includes(song.id)
                                                                ? 'bg-green-600 border-green-600'
                                                                : 'border-gray-500'
                                                        }`}>
                                                            {setlistFormData.selectedSongs.includes(song.id) && (
                                                                <Check className="w-3 h-3 text-white" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </form>
                        <DialogFooter className="mt-6 flex-shrink-0">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={closeSetlistDialog}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                onClick={handleSetlistSubmit}
                                disabled={!setlistFormData.name.trim() || setlistFormData.selectedSongs.length === 0}
                                className="bg-green-600 hover:bg-green-700"
                            >
                                {editingSetlist ? 'Update Setlist' : 'Create Setlist'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {/* Add/Edit Song Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className={`bg-gray-900 border-gray-700 text-white ${multiSongMode ? 'max-w-2xl' : 'max-w-md'}`}>
                    <DialogHeader>
                        <DialogTitle>{editingSong ? 'Edit Song' : multiSongMode ? 'Add Multiple Songs' : 'Add New Song'}</DialogTitle>
                        <DialogDescription>
                            {editingSong ? 'Update your song details' : multiSongMode ? 'Add multiple songs to your catalogue' : 'Add a new song to your catalogue'}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {multiSongMode && addedSongs.length > 0 && (
                        <div className="space-y-3 mb-4">
                            <h4 className="text-white font-medium">Added Songs ({addedSongs.length})</h4>
                            <div className="max-h-48 overflow-y-auto space-y-2">
                                {addedSongs.map((song) => (
                                    <div key={song.id} className="bg-gray-800/50 rounded-lg p-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3 flex-1">
                                                <button
                                                    onClick={() => toggleSongExpansion(song.id)}
                                                    className="text-gray-400 hover:text-white"
                                                >
                                                    {song.expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                </button>
                                                <div className="flex-1">
                                                    <p className="text-white font-medium">{capitalizeWords(song.title)}</p>
                                                    {song.expanded && (
                                                        <div className="mt-2 space-y-1 text-sm text-gray-300">
                                                            <p>Artist: {capitalizeWords(song.original_artist || song.artist || 'Unknown Artist')}</p>
                                                            <p>Price: {format(parseFloat(song.price) || 0)}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeAddedSong(song.id)}
                                                className="text-red-400 hover:text-red-300 ml-2"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="title">Song Title *</Label>
                            <Input
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="Enter song title"
                                required
                                className="bg-gray-800 border-gray-600 text-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="original_artist">Original Artist *</Label>
                            <Input
                                id="original_artist"
                                name="original_artist"
                                value={formData.original_artist}
                                onChange={handleInputChange}
                                placeholder="Enter original artist name"
                                required
                                className="bg-gray-800 border-gray-600 text-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="price">
                                Price ({format(0)}) *
                                <span className="text-xs text-gray-400 ml-2">Minimum: {effectiveMinDisplay}</span>
                            </Label>
                            <Input
                                id="price"
                                name="price"
                                type="number"
                                step="0.01"
                                min={effectivePriceMin}
                                value={formData.price}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setFormData(prev => ({ ...prev, price: value }));
                                }}
                                placeholder={effectivePriceMin.toFixed(2)}
                                required
                                className={`bg-gray-800 border-gray-600 text-white ${
                                    formData.price && parseFloat(formData.price) < effectivePriceMin
                                        ? 'border-red-500 focus:border-red-500' : ''
                                }`}
                            />
                            {formData.price && (() => {
                                const numValue = parseFloat(formData.price);
                                const isTestSong = formData.title.trim().toLowerCase() === 'test' && 
                                                  formData.original_artist.trim().toLowerCase() === 'test';
                                
                                if (!isTestSong && numValue < minimumPrice) {
                                    return (
                                        <p className="text-sm text-red-400 mt-1 flex items-start">
                                            <span className="mr-1">⚠️</span>
                                            <span>Minimum price is {formattedMinimum}. We recommend {format(minimumPrice)}-{format(minimumPrice + 1)} per request for better earnings.</span>
                                        </p>
                                    );
                                } else if (isTestSong && numValue < testSongMinimum) {
                                    return (
                                        <p className="text-sm text-red-400 mt-1 flex items-start">
                                            <span className="mr-1">⚠️</span>
                                            <span>Minimum price for test songs is {format(testSongMinimum)}.</span>
                                        </p>
                                    );
                                } else if (!isTestSong && numValue >= minimumPrice) {
                                    return (
                                        <p className="text-xs text-green-400 mt-1">✓ Price meets minimum requirement</p>
                                    );
                                }
                                return null;
                            })()}
                        </div>

                        <DialogFooter className="flex justify-between">
                            <Button type="button" variant="outline" onClick={closeDialog}>
                                Cancel
                            </Button>
                            <div className="flex space-x-2">
                                {multiSongMode && (
                                    <Button 
                                        type="button" 
                                        onClick={saveAllSongs}
                                        disabled={!formData.title.trim() || !formData.original_artist.trim() || !formData.price}
                                        className="bg-green-600 hover:bg-green-700"
                                    >
                                        Save and Close
                                    </Button>
                                )}
                                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                                    {editingSong ? 'Update Song' : multiSongMode ? 'Add Another Song' : 'Add Song'}
                                </Button>
                            </div>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Setlist Wizard */}
            {(() => {
                console.log('🔍 SetlistPage: Rendering SetlistWizard with showSetlistWizard:', showSetlistWizard);
                return (
                    <SetlistWizard
                        isOpen={showSetlistWizard}
                        onClose={() => {
                            console.log('🔍 SetlistWizard: onClose called');
                            setShowSetlistWizard(false);
                        }}
                        onComplete={handleWizardComplete}
                    />
                );
            })()}

            {/* Add Song Wizard */}
            <AddSongWizard
                isVisible={showSongWizard}
                onComplete={handleSongWizardComplete}
                onDismiss={() => {
                    setShowSongWizard(false);
                    try {
                        localStorage.setItem(ADD_SONG_WIZARD_DISMISSED_KEY, 'true');
                    } catch (_) {
                        /* ignore */
                    }
                }}
            />
        </>
    );
};

export default SetlistPage;